import { isBefore, parseISO } from 'date-fns';

import {
  LicenseDemoPeriodModel,
  LicenseModel,
  LicenseType,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { isIframedByExactStudio } from '../../../react/utils/iframe';
import { CerbosFeature } from '../../services/cerbos/features';
import { ProjectStore } from '../../stores/project-store';
import { UserStore } from '../../stores/user-store';
import { AngularState, ThemeManager } from '../../theming/theme-manager';
import { assertNever, isDefined } from '../variables';

const statesExcludedFromLicenseCheck = [
  'sky.admin.companyprojects',
  'sky.admin.projects',
  'sky.admin.users.add',
  'sky.admin.users.invitecompanyadmin',
  'sky.admin.users.overview',
  'sky.admin.users.overview.edit',
  'sky.dashboard',
  'sky.newcompanyadmin',
  'sky.project',
  'sky.companyregister',
  'sky.verify',
  'sky.companyregisterdesign',
] as AngularState[];

/**
 * A list of states only accessible to users with a valid Design license.
 */
const validStatesForDesignLicense = [
  'sky.project.details',
  'sky.project.details.email',
  'sky.project.details.filemanager',
  'sky.project.details.projectusers',
  'sky.project.details.settings',
  'sky.project.details.userlist',
  'sky.viewer',
] as AngularState[];

/**
 * Returns a value indicating whether the current user has a license that allows access to the
 * specified view for the current project.
 */
const hasAccessToViewByUserLicenses = (view: AngularState) => {
  if (UserStore.instance.isOrganizationAdmin() || statesExcludedFromLicenseCheck.includes(view)) {
    return true;
  }

  return (
    // We don't use findUserLicensesByType() here as that function ignores all global licenses for
    // attendees. Attendees need the license only to check whether access is given to the user for a
    // specific view.
    ProjectStore.instance.project?.companyUserLicenses?.some(
      (license) =>
        license.companyId === ProjectStore.instance.project!.companyId &&
        licensePermissionChecks.isViewAllowedByLicense(view, license),
    ) ?? false
  );
};

/**
 * Returns a value indicating whether current user has a license that allows access to the specified
 * view.
 */
const isViewAllowedByLicense = (view: AngularState, license: LicenseModel) => {
  const hasActiveSubscription = isDefined(license.startedSubscriptionAt);
  const hasDemoPeriods = (license.licenseDemoPeriods ?? []).length > 0;
  const hasValidDemoPeriod = license.licenseDemoPeriods?.some(isDemoPeriodValid) ?? false;

  if (hasActiveSubscription || !hasDemoPeriods || hasValidDemoPeriod) {
    switch (license.licenseType) {
      case 'DESIGN': {
        return validStatesForDesignLicense.includes(view);
      }

      case 'PORTAL':
        // This license has access to all views.
        return true;

      default:
        assertNever(license.licenseType);
    }
  }

  return false;
};

/**
 * Returns a value indicating whether the current user has a license that allows access to the
 * specified feature.
 */
const hasAccessToFeatureByUserLicenses = (feature: CerbosFeature) => {
  switch (feature) {
    case 'feature:viewer:chips:as_built':
      return licensePermissionChecks.hasValidLicense('PORTAL');
    default:
      return true;
  }
};

/**
 * Return a list of licenses matching the specified license type.
 *
 * For attendees, the license must be explicitly created for the user. Attendees need a
 * company-only license in order to display menu items, but attendees should not be able to use the
 * tools. That's why we in this function only return licenses for attendees that matches the user ID
 * in the license.
 */
const findUserLicensesByType = (licenseType: LicenseType) => {
  return (
    ProjectStore.instance.project?.companyUserLicenses?.filter((license) => {
      if (
        license.licenseType !== licenseType ||
        license.companyId !== ProjectStore.instance.project?.companyId
      ) {
        return false;
      }

      if (
        UserStore.instance.isActiveRoleProjectUser() &&
        license.userId !== UserStore.instance.getActiveUserId()
      ) {
        return false;
      }

      return true;
    }) ?? []
  );
};

/**
 * Returns a value indicating whether the current user has the specified license type for the
 * company that the current project belongs to, and that the license is valid for the current date.
 */
const hasValidLicense = (licenseType: LicenseType) => {
  // MOBA company administrators always have a valid Design license. No Design license exist in the
  // database for MOBA users.
  //
  // TODO: Maybe the backend should be responsible to setup a license for each company admin. Then
  // TODO: this check can be removed.
  if (
    licenseType === 'DESIGN' &&
    ThemeManager.instance.isMobaTheme() &&
    UserStore.instance.isCompanyAdmin()
  ) {
    return true;
  }

  if (licenseType === 'DESIGN' && isIframedByExactStudio()) {
    return true;
  }

  return licensePermissionChecks
    .findUserLicensesByType(licenseType)
    .some(licensePermissionChecks.isLicenseValid);
};

/**
 * Returns a value indicating whether the current user has a specified license type for the
 * company that the current project belongs to, and that the license has expired.
 */
const hasExpiredLicense = (licenseType: LicenseType) => {
  return isDefined(licensePermissionChecks.getExpiredLicense(licenseType));
};

/**
 * Returns the first expired license of the specified type, or undefined if none is found.
 */
const getExpiredLicense = (licenseType: LicenseType) => {
  return licensePermissionChecks
    .findUserLicensesByType(licenseType)
    .find((x) => !licensePermissionChecks.isLicenseValid(x));
};

/**
 * Returns a value indicating whether the specified demo period is valid (has not expired).
 */
const isDemoPeriodValid = (demoPeriod: LicenseDemoPeriodModel) =>
  isBefore(new Date(), parseISO(demoPeriod.validUntil));

/**
 * Returns true if subscription is started or if no demo periods exist. Otherwise true is returned
 * if at least one demo period is valid.
 */
const isLicenseValid = (license: LicenseModel) => {
  if (isDefined(license.startedSubscriptionAt)) {
    return true;
  }

  return isDefined(license.licenseDemoPeriods) && license.licenseDemoPeriods.length > 0
    ? license.licenseDemoPeriods.some(licensePermissionChecks.isDemoPeriodValid)
    : true;
};

export const licensePermissionChecks = {
  findUserLicensesByType,
  getExpiredLicense,
  hasAccessToFeatureByUserLicenses,
  hasAccessToViewByUserLicenses,
  hasExpiredLicense,
  hasValidLicense,
  isDemoPeriodValid,
  isLicenseValid,
  isViewAllowedByLicense,
};
